import React, { Component } from 'react';
import { connect } from 'react-redux';
import Dropzone from 'react-dropzone';

import { isDdnyUser } from '../../../../utils/auth';
import { 
	ddnyFileTypeLabel,
	ddnyMediaSrc,
	ddnyTitle, 
} from '../../../../utils/ddny';
import { getFileType } from '../../../../utils/files';
import { formatFilesize } from '../../../../utils/formatting';

import DirectoryMedia from './DirectoryMedia';
import Icon from '../../../common/icons/Icon';
import LoadingIcon from '../../../common/icons/LoadingIcon';

import styles from './_styles/ProductMedia.module.scss';

import * as ddnyActionCreators from '../../../../actions/ddny';
const allActionCreators = Object.assign({}, ddnyActionCreators);

export class ProductMedia extends Component {

	constructor(props) {
	
		super(props);

		this.state = {
			
			viewIndex: this.firstVisualIndex(),

			filesType: '',

			addingFiles: false,

			dragCount: 0,

			inputFiles: [],
			errorFiles: '',

			uploadPending: false,
			uploadError: null,

			mediaLoading: false,
		};

		this.FILE_TYPES = [ '', 'image', 'video', 'other' ];

		this.fileDropRef = React.createRef();
	}

	firstVisualIndex() {
		if(!this.props.product?.media) { return 0; }
		for(let i = 0; i < this.props.product.media.length; i++) {
			if(this.props.product.media[i].file_type === 'image' || this.props.product.media[i].file_type === 'video') {
				return i;
			}
		}
		return 0;
	}

	hasMedia() {
		return this.props.product?.media && this.props.product.media.length > 0;
	}

	filteredMedia() {

		// Restrict 3DM and STL files
		const RESTRICTED_MIMES = [
			'x-world/x-3dmf',
			'model/3dm',
			'model/vnd.3dm+json',
			'model/vnd.rhinoceros.3d',

			'model/stl',
			'model/x.stl-binary',
			'application/sla',
			'application/vnd.ms-pki.stl',
			'application/x-navistyle',
		]

		if(!this.hasMedia()) {
			return [];
		}

		let filteredMedia = isDdnyUser({ nyLimited: true }) ? this.props.product.media : this.props.product.media.filter(md => !RESTRICTED_MIMES.includes(md.file_mime));

		if(!this.state.filesType) {
			return filteredMedia;
		}
		
		return filteredMedia.filter(media => media.file_type === this.state.filesType);
	}

	visualMedia() {
		if(!this.hasMedia()) {
			return [];
		}
		return this.props.product.media.filter(media => media.file_type === 'image' || media.file_type === 'video');
	}

	isVisual(media) {
		if(!media) {
			return false;
		}
		return !!(media.file_type === 'image' || media.file_type === 'video');
	}

	getDisplayWrapperClass() {
		try {
			if(this.hasMedia() && this.state.viewIndex < this.props.product.media.length) {
				switch(this.props.product.media[this.state.viewIndex].file_type) {
					case 'image':
						return styles.imageWrapper;
					case 'video':
						return styles.videoWrapper;
					default:
						return styles.imageWrapper;
				}
			}
		} catch(err) {
			return styles.imageWrapper;
		}
	}

	handleTypeChange(evt) {
		this.setState({ filesType: evt.target.value });
	}

	toggleAddFiles() {
		this.setState({ addingFiles: !this.state.addingFiles });
	}

	dragEnterInput(evt) {
		for(const tp of evt.dataTransfer.types) {
			if(tp === 'Files') {
				this.setState({ dragCount: this.state.dragCount + 1 });
				break;
			}
		}
	}

	dragLeaveInput(evt) {
		this.setState({ dragCount: Math.max(this.state.dragCount - 1, 0) });
	}

	getFileError(file, mbSizeLimit = 100) {

		if(!file) {
			return 'Invalid file';
		}
	
		const flExtension = file.name.substring(file.name.lastIndexOf('.') + 1).toLowerCase();
		const flMime = getFileType(file);
		const flSize = file.size;
		
		// Default upload limit: 100MB
		const maxFileSize = mbSizeLimit * 1024 * 1024; 
	
		if(flSize > maxFileSize) {
			return 'File must be less than 100MB.';
		}
	
		return '';
	}

	getFileType(file) {
		
		const imgMimes = [
			'image/jpeg',
			'image/png',
			'image/gif',
			'image/webp',
		];

		const videoMimes = [
			'video/mp4',
			'video/webm',
			'video/mpeg',
		];

		const flMime = getFileType(file);

		if(imgMimes.includes(flMime)) {
			return 'image';
		} else if(videoMimes.includes(flMime)) {
			return 'video';
		}
		return 'other';
	}

	async dragDropInput(files) {

		let fileError = '';
		for(const fl of files) {
			const fileErr = this.getFileError(fl);
			if(fileErr) {
				fileError = fileErr;
				break;
			}
		}

		this.setState({
			errorFiles: fileError,
			dragCount: 0,
		});

		if(fileError) {
			return null;
		}

		const allFiles = [ ...this.state.inputFiles, ...files ];

		this.setState({
			inputFiles: allFiles,
		});
	}

	removeFiles(evt) {
		evt.stopPropagation();
		this.setState({
			inputFiles: [],
			errorFiles: '',
			uploadError: null,
		});
	}

	removeFile(idx) {
		const files = this.state.inputFiles.filter((_, index) => index !== idx);
		this.setState({
			inputFiles: files,
			uploadError: null,
		}, () => {
			let fileError = '';
			for(const fl of files) {
				const fileErr = this.getFileError(fl);
				if(fileErr) {
					fileError = fileErr;
					break;
				}
			}

			this.setState({
				errorFiles: fileError,
			});
		});
	}

	async initiateUpload(evt) {
		
		if(evt) { 
			evt.preventDefault(); 
			evt.stopPropagation();
		}

		this.setState({ 
			uploadPending: true,
			uploadError: null,
		});

		try {
			for(const fl of this.state.inputFiles) {

				const uploadResp = await this.props.ddnyUploadMedia(this.props.product.id, fl)
						
				const updateData = {
					product_id: this.props.product.id,
					s3_key: uploadResp.key,
					file_name: fl.name,
					file_type: this.getFileType(fl),
					file_mime: getFileType(fl),
				};
			
				await this.props.ddnyCreateMedia(updateData);
			}

			this.setState({ inputFiles: [] });

			await this.props.refresh();

		} catch(err) {
			console.error(err);
			// err can be null/undefined from a rejected fetch, so read it defensively —
			// `err.msg` on a bare null throws inside the catch and loses the failure.
			this.setState({ uploadError: err?.msg || 'An unexpected error occurred' });
		} finally {
			this.setState({ uploadPending: false });
		}
	}

	setViewIndex(idx) {
		this.setState({ viewIndex: idx });
	}

	setMediaLoading(loadingVal) {
		this.setState({ mediaLoading: loadingVal });
	}

	getFileTypeCount() {
		if(!this.hasMedia()) {
			return 0;
		}
		return this.filteredMedia().length || 0;
	}

	render() {

		let last_seq = null;

		if(!this.props.product) {
			return null;
		}

		return <div className={styles.ProductMedia}>
			<div className={styles.mediaWrapper}>
				<div className={styles.viewWrapper}>
					<div className={styles.viewSection}>
						<div className={styles.display}>
							{this.visualMedia().length > 0
								?	<div className={this.getDisplayWrapperClass()}>
										{this.props.product.media[this.state.viewIndex].file_type === 'image'
											?	<img alt={ddnyTitle(this.props.product)} src={ddnyMediaSrc(this.props.product.media[this.state.viewIndex])} />
											: 	<video key={this.state.viewIndex} controls autoPlay muted playsInline loop>
													<source 
														src={ddnyMediaSrc(this.props.product.media[this.state.viewIndex])} 
														type={this.props.product.media[this.state.viewIndex].file_mime} />
												</video>
										}
									</div>
								:	<div className={styles.imageWrapper}>
										<img alt={ddnyTitle(this.props.product)} src='/img/ddny/no-image.jpg' />
									</div>
							}
						</div>
						{this.visualMedia().length > 0 && (
							<div className={styles.thumbnails}>
								{this.props.product.media?.map((md, m) => {

									if(!this.isVisual(md)) {
										return null;
									}

									return <div key={m} className={`${styles.thumb} ${this.state.viewIndex === m ? styles.active : ''}`} onClick={() => this.setViewIndex(m)}>
										{md.file_type === 'image'
											?	<div className={styles.thumbImgWrapper}>
													<img alt={ddnyTitle(this.props.product)} src={ddnyMediaSrc(md)} />
												</div>
											: 	<div className={styles.thumbVideoWrapper}>
													<Icon value='video' />
												</div>
										}
									</div>
								})}
							</div>
						)}
					</div>
					<div className={styles.directorySection}>
						<div className={styles.directory}>
							<div className={styles.headerWrapper}>
								<div className={styles.headerTitle}>
									<div className={styles.value}>{ddnyFileTypeLabel(this.state.filesType)}</div>
									<div className={styles.count}>({this.getFileTypeCount()})</div>
								</div>
								<div className={styles.headerAction}>
									<div className={styles.label}>File type</div>
									<div className={styles.inputWrapper}>
										<select
											value={this.state.filesType}
											onChange={this.handleTypeChange.bind(this)}>
											{this.FILE_TYPES.map((ft, j) => {
												return <option key={j} value={ft}>{ddnyFileTypeLabel(ft)}</option>
											})}
										</select>
									</div>
								</div>
							</div>
							<div className={styles.bodyWrapper}>
								{this.filteredMedia().length === 0
									?	<div className={styles.noMedia}>No files</div>
									:	<div className={styles.filesWrapper}>
											{this.filteredMedia().map((media, i) => {

												const last_seq_val = last_seq;
												last_seq = media.sequence;

												const comp =  <DirectoryMedia
																	key={i}
																	last_sequence={last_seq_val}
																	product={this.props.product}
																	media={media}
																	readOnly={this.props.readOnly}
																	refresh={this.props.refresh}
																	setMediaLoading={this.setMediaLoading.bind(this)} />
												
												return comp;
											})}
										</div>
								}
							</div>
						</div>
						{this.state.mediaLoading && (
							<div className={styles.mediaLoading}>
								<div className={styles.mediaLoadingIcon}>
									<LoadingIcon />
								</div>
							</div>
						)}
					</div>
				</div>
				{!this.props.readOnly && (
					<div className={styles.editSection}>
						{!this.state.addingFiles
							?	<div className={styles.addPromptWrapper}>
									<button 
										type='button'
										className={`${styles.addPrompt} actionButton`}
										onClick={this.toggleAddFiles.bind(this)}>
										+ Add Files
									</button>
								</div>
							:	<div className={styles.addingWrapper}>
									<div className={styles.uploadWrapper}>
										<Dropzone onDrop={acceptedFiles => this.dragDropInput(acceptedFiles)}>
											{({getRootProps, getInputProps}) => (
												<section>
													<div {...getRootProps()}>
														<input {...getInputProps()} />
														<div 
															ref={this.fileDropRef}
															className={`${styles.dropWrapper} ${this.state.dragCount ? styles.hoverFile : ''}`}
															onDragEnter={this.dragEnterInput.bind(this)}
															onDragLeave={this.dragLeaveInput.bind(this)}>

															{this.state.inputFiles.length > 0 ?
																<>
																	{this.state.inputFiles.map((fl, i) => {
																		return <div key={i} className={styles.dropFileDisplay}>
																			<div className={styles.dropFileDetailsWrapper}>
																				<div className={`${styles.nameLabel} EllipsisElement`}>{fl.name}</div>
																				<div className={styles.metaLabel}>{formatFilesize(fl.size)}</div>
																				<div className={styles.typeLabel}>{ddnyFileTypeLabel(this.getFileType(fl), true)}</div>
																				<div className={styles.removeLabel} onClick={() => this.removeFile(i)}>Remove</div>
																			</div>
																			<div className={styles.dropFileCloseWrapper} onClick={() => this.removeFile(i)}>
																				<div className='FlexCenter'>&times;</div>
																			</div>
																		</div>
																	})}
																</> :
																<div className={styles.dropPrompt}>Drag and drop or click here to upload files</div>
															}
																
															{this.state.errorFiles && (<div className={styles.dropError}>{this.state.errorFiles}</div>)}

															{this.state.uploadError && (<div className={styles.dropError}>{this.state.uploadError}</div>)}

															<div className={styles.dropActions}>
																{this.state.inputFiles.length === 0 ?
																	<button 
																		className={`${styles.dropButton} actionButton`}
																		type='button'>
																			{'Select File'}
																	</button> :
																	<button 
																		className={`${styles.dropButton} actionButton`}
																		onClick={this.initiateUpload.bind(this)}
																		disabled={!!this.state.errorFiles || this.state.uploadPending}
																		type='button'>
																			{this.state.uploadPending
																				? 'Uploading...'
																				: (this.state.inputFiles.length === 1 ? 'Upload File' : 'Upload Files')}
																	</button>
																}
															</div>
														</div>
													</div>
												</section>
											)}
										</Dropzone>
									</div>
								</div>
						}
					</div>
				)}
			</div>
		</div>;
	}
}

function mapStateToProps(state) {
	return {

	};
}

export default connect(mapStateToProps, allActionCreators)(ProductMedia);